import React, { useState } from 'react';
import { Theme, TextField, Button, Typography, Box, Paper } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, NavLink } from 'react-router-dom';

import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../../services/firebase.config';
import { setDoc, getDoc, doc } from 'firebase/firestore';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: 'white',
    boxShadow: '0 8px 32px rgba(44, 62, 80, 0.15)',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  logo: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: '#6c757d',
    fontSize: '1rem',
  },
  form: {
    width: '100%',
  },
  textFieldContainer: {
    marginBottom: theme.spacing(3),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3498db',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2980b9',
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#2c3e50',
      '&.Mui-focused': {
        color: '#2980b9',
      },
    },
  },
  signupButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    padding: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: '#3498db',
    },
  },
  loginLink: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  link: {
    color: '#2980b9',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  backLink: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(3),
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: 'white',
    },
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
  successAlert: {
    marginBottom: theme.spacing(2),
  },
}));

const Signup = () => {
  const history = useHistory();
  const classes = useStyles();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update the user's display name
      await updateProfile(user, {
        displayName: userName,
      });

      // Add user to Firestore
      const userRef = doc(db, 'users', user.uid);
      const docSnapshot = await getDoc(userRef);

      if (!docSnapshot.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          name: userName,
          email: user.email,
        });
      }

      setSuccess('Account created successfully! Redirecting to login...');

      // Redirect after a brief delay
      setTimeout(() => {
        history.replace('/login');
      }, 2000);
    } catch (authError) {
      const firebaseError = authError as { code?: string; message?: string };
      const errorCode = firebaseError.code;
      const errorMessage = firebaseError.message;
      console.log(errorCode, errorMessage);

      // Provide user-friendly error messages
      if (errorCode === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (errorCode === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (errorCode === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('An error occurred. Please try again.');
      }
    }
  };

  return (
    <div className={classes.root}>
      <NavLink to="/" className={classes.backLink}>
        ← Back to Home
      </NavLink>

      <Paper className={classes.paper} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.logo}>
            BLUESTREAM
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Create your account
          </Typography>
        </Box>

        <form onSubmit={onSubmit} className={classes.form}>
          {error && (
            <Alert severity="error" className={classes.errorAlert}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" className={classes.successAlert}>
              {success}
            </Alert>
          )}

          <div className={classes.textFieldContainer}>
            <TextField
              type="text"
              value={userName}
              onChange={e => setUserName(e.target.value)}
              required
              label="Full Name"
              placeholder="Enter your full name"
              variant="outlined"
              fullWidth
              className={classes.textField}
            />
          </div>

          <div className={classes.textFieldContainer}>
            <TextField
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              label="Email Address"
              placeholder="Enter your email"
              variant="outlined"
              fullWidth
              className={classes.textField}
            />
          </div>

          <div className={classes.textFieldContainer}>
            <TextField
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              label="Password"
              placeholder="Enter your password"
              variant="outlined"
              fullWidth
              className={classes.textField}
              helperText="Password must be at least 6 characters"
            />
          </div>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={classes.signupButton}
            disabled={!email || !password || !userName}
          >
            Create Account
          </Button>
        </form>

        <div className={classes.loginLink}>
          <Typography variant="body2" style={{ color: '#6c757d' }}>
            Already have an account?{' '}
            <NavLink to="/login" className={classes.link}>
              Sign in here
            </NavLink>
          </Typography>
        </div>
      </Paper>
    </div>
  );
};

export default Signup;
